import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./themeupdate.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";

const STATES_LIST = [
  "ANDHRA PRADESH", "ARUNACHAL PRADESH", "ASSAM", "BIHAR", "CHHATTISGARH",
  "GOA", "GUJARAT", "HARYANA", "HIMACHAL PRADESH", "JHARKHAND",
  "KARNATAKA", "KERALA", "MADHYA PRADESH", "MAHARASHTRA", "MANIPUR",
  "MEGHALAYA", "MIZORAM", "NAGALAND", "ODISHA", "PUNJAB",
  "RAJASTHAN", "SIKKIM", "TAMIL NADU", "TELANGANA", "TRIPURA",
  "UTTAR PRADESH", "UTTARAKHAND", "WEST BENGAL"
];

const QUESTION_TYPES = [
  { value: "radioselect", label: "Multiple Choice (3 Options)" },
  { value: "truefalse", label: "True/False" },
  { value: "typetext", label: "Text Input" }
];

const TOUR_STORAGE_KEY = "admin28states_tour_seen";

// Guided walkthrough steps. `tab: null` means the step works regardless of
// which tab is active; otherwise the tour switches to that tab before
// looking for `selector` in the DOM. Steps whose selector isn't found (e.g.
// the state summary before any questions exist) are skipped automatically.
const TOUR_STEPS = [
  {
    tab: null,
    selector: ".themeupdate-card-header",
    title: "Welcome to 28 States Game Management",
    description:
      "This is where you manage everything for the 28 States game — questions, state GIFs, map background, background sound, and the secret message feature. Let's take a quick look around. You can replay this tour anytime with the \"Take a Tour\" button here.",
  },
  {
    tab: null,
    selector: ".themeupdate-tabs",
    title: "Tabs",
    description:
      "Switch between the different sections of this game from here: Questions, State GIFs, Map Background, Secret Message, and Background Sound.",
  },
  {
    tab: "questions",
    selector: ".themeupdate-qps-row",
    title: "Questions per State",
    description:
      "Pick how many questions (1–5) are actually used per state. Only the first that many, in order, are shown below, used in the game, and included in CSV export — the rest stay saved but unused until you raise the count.",
  },
  {
    tab: "questions",
    selector: ".themeupdate-missing-banner",
    title: "Missing Questions Check",
    description:
      "A quick health-check right under the count selector: it lists exactly which states still fall short of the required count, so you know who needs attention before players hit a dead end.",
  },
  {
    tab: "questions",
    selector: ".themeupdate-actions-row",
    title: "Add or Import Questions",
    description:
      "Add a question one at a time, or upload a CSV to bulk-import many at once. You can also export your current questions or download a sample CSV to see the required format. Heads up: importing a CSV replaces ALL existing questions, so export a backup first if you're not sure.",
  },
  {
    tab: "questions",
    selector: ".themeupdate-table-section",
    title: "Questions List",
    description:
      "All your questions show up here, 10 at a time — use the edit/delete icons on each row to manage a question, and the Prev/Next buttons at the bottom to page through the rest.",
  },
  {
    tab: "gifs",
    selector: ".themeupdate-gifs-section",
    title: "State GIFs",
    description:
      "Upload a short celebration GIF for each state — it plays when a player answers that state's questions correctly.",
  },
  {
    tab: "mapbg",
    selector: ".themeupdate-gifs-section",
    title: "Map Background",
    description:
      "Upload a custom background image for the map card on the game screen, and optionally apply the same image behind the question panel.",
  },
  {
    tab: "secret",
    selector: ".themeupdate-gifs-section",
    title: "Secret Message",
    description:
      "Turn this on to hide a 28-letter message that players uncover one letter at a time as they complete each state. Spaces between words are allowed and don't count toward the 28 — fully revealed once all 28 states are done.",
  },
  {
    tab: "sound",
    selector: ".themeupdate-gifs-section",
    title: "Background Sound",
    description:
      "Turn this on to auto-play background music on the game screen. Players get a speaker icon to mute or unmute it — the toggle here just controls whether it's offered at all.",
  },
];

/** Position the tooltip near the highlighted element, clamped to stay on-screen. */
function computeTourTooltipStyle(rect) {
  const TOOLTIP_WIDTH = 320;
  const MARGIN = 16;
  let top = rect.bottom + 14;
  if (top + 200 > window.innerHeight) top = Math.max(rect.top - 210, MARGIN);
  let left = rect.left;
  if (left + TOOLTIP_WIDTH + MARGIN > window.innerWidth) left = window.innerWidth - TOOLTIP_WIDTH - MARGIN;
  left = Math.max(left, MARGIN);
  return { top, left };
}

/**
 * Uploads a file straight to S3 via a presigned URL instead of sending the
 * bytes through our own API — API Gateway has a hard 10MB request payload
 * ceiling (non-negotiable, can't be raised), and a file at our own 10MB
 * limit can already exceed that once in-transit encoding overhead is added.
 * Returns the S3 key to save in the settings record.
 */
async function uploadFileDirectToS3({ adminToken, organizationId, sessionId, currentTheme, kind, file }) {
  const urlRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getUploadUrl`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
    body: JSON.stringify({ organizationId, sessionId, currentTheme, kind, fileName: file.name }),
  });
  const urlData = await urlRes.json();
  if (!urlData.success) throw new Error(urlData.message || "Failed to prepare upload");

  const putRes = await fetch(urlData.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": urlData.contentType },
    body: file,
  });
  if (!putRes.ok) throw new Error("Failed to upload file to storage");

  return urlData.key;
}

const emptyQuestion = () => ({
  id: null,
  question_title: "",
  question: "",
  image: null,
  imagePreview: "",
  option_one: "",
  option_two: "",
  option_three: "",
  correct_answer: "",
  answer_id: "",
  did_you_know: "",
  type_question: "radioselect",
  state: "",
  sort_order: 0
});

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme } = useSelector((state) => state.theme);

  const [activeTab, setActiveTab] = useState("questions");
  const [questions, setQuestions] = useState([]);
  const [questionsPage, setQuestionsPage] = useState(1);
  const QUESTIONS_PAGE_SIZE = 10;
  const [questionsPerStateCount, setQuestionsPerStateCount] = useState(1);
  const [questionsPerStateSaving, setQuestionsPerStateSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState(emptyQuestion());
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState("");
  const csvInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // State GIFs tab state
  const [stateGifs, setStateGifs] = useState({});
  const [gifLoading, setGifLoading] = useState(false);
  const [gifUploading, setGifUploading] = useState({});

  // Map BG tab state
  const [mapBgImage, setMapBgImage] = useState("");
  const [mapBgApplyQuestion, setMapBgApplyQuestion] = useState(false);
  const [mapBgFile, setMapBgFile] = useState(null);
  const [mapBgPreview, setMapBgPreview] = useState("");
  const [mapBgLoading, setMapBgLoading] = useState(false);
  const [mapBgUploading, setMapBgUploading] = useState(false);
  const mapBgInputRef = useRef(null);

  // Secret Message tab state
  const [secretEnabled, setSecretEnabled] = useState(false);
  const [secretMessage, setSecretMessage] = useState("");
  const [secretLoading, setSecretLoading] = useState(false);
  const [secretSaving, setSecretSaving] = useState(false);

  // Background Sound tab state
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [soundFile, setSoundFile] = useState(null);
  const [soundName, setSoundName] = useState("");
  const [soundPreviewUrl, setSoundPreviewUrl] = useState("");
  const [soundLoading, setSoundLoading] = useState(false);
  const [soundUploading, setSoundUploading] = useState(false);
  const soundInputRef = useRef(null);

  useEffect(() => {
    if (currentTheme && adminToken) {
      loadQuestionsPerStateSettings();
      loadQuestions();
      loadStateGifs();
      loadMapBgSettings();
      loadSecretMessageSettings();
      loadSoundSettings();
    }
  }, [currentTheme, adminToken]);

  // Guided walkthrough
  const [tourRunning, setTourRunning] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourRect, setTourRect] = useState(null);
  const preTourTabRef = useRef(null);

  const endTour = useCallback(() => {
    setTourRunning(false);
    setTourRect(null);
    // Return the admin to whichever tab they were on before the tour moved
    // them around, instead of stranding them on the tour's last stop.
    if (preTourTabRef.current) {
      setActiveTab(preTourTabRef.current);
      preTourTabRef.current = null;
    }
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, "1");
    } catch (_) {
      /* ignore */
    }
  }, []);

  // Pure step increment — going one index past the last step is handled by
  // the navigation effect below (its `if (!step)` branch calls endTour()),
  // so this never needs to know when the tour is over.
  const nextTourStep = useCallback(() => {
    setTourStep((i) => i + 1);
  }, []);

  // Auto-start once per browser, the first time an admin lands on this page.
  useEffect(() => {
    try {
      if (!localStorage.getItem(TOUR_STORAGE_KEY)) {
        const t = setTimeout(() => {
          preTourTabRef.current = "questions"; // the default tab at mount
          setTourStep(0);
          setTourRunning(true);
        }, 700);
        return () => clearTimeout(t);
      }
    } catch (_) {
      /* ignore */
    }
    return undefined;
  }, []);

  // Switch to the step's tab (if needed), then locate and measure its target
  // element. Steps whose element isn't in the DOM (e.g. the state summary
  // before any questions exist) are skipped forward automatically.
  useEffect(() => {
    if (!tourRunning) return undefined;
    const step = TOUR_STEPS[tourStep];
    if (!step) {
      endTour();
      return undefined;
    }
    if (step.tab && step.tab !== activeTab) {
      setActiveTab(step.tab);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (!el) {
        nextTourStep();
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "auto" });
      requestAnimationFrame(() => {
        if (!cancelled) setTourRect(el.getBoundingClientRect());
      });
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tourRunning, tourStep, activeTab, endTour, nextTourStep]);

  // Keep the highlight aligned with its target if the viewport changes size.
  useEffect(() => {
    if (!tourRunning) return undefined;
    const step = TOUR_STEPS[tourStep];
    if (!step) return undefined;
    const onResize = () => {
      const el = document.querySelector(step.selector);
      if (el) setTourRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [tourRunning, tourStep]);

  const startTour = () => {
    preTourTabRef.current = activeTab;
    setTourStep(0);
    setTourRunning(true);
  };

  const loadStateGifs = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setGifLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getStateGifs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const data = await res.json();
      if (data.success) setStateGifs(data.gifs || {});
    } catch (e) {
      console.error("loadStateGifs error:", e);
    } finally {
      setGifLoading(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  const handleGifUpload = async (state, file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "gif") {
      Swal.fire("Error", "Only GIF files are allowed", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Error", "GIF must be less than 5MB", "error");
      return;
    }
    setGifUploading((prev) => ({ ...prev, [state]: true }));
    try {
      const fd = new FormData();
      fd.append("organizationId", organizationId || "admin");
      fd.append("sessionId", sessionId || "admin");
      fd.append("currentTheme", currentTheme);
      fd.append("state", state);
      fd.append("gif", file);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveStateGif`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setStateGifs((prev) => ({ ...prev, [state]: data.gif_url }));
        Swal.fire({ icon: "success", title: "Saved!", text: `GIF uploaded for ${state}`, timer: 1500, showConfirmButton: false });
      } else {
        throw new Error(data.message);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
    } finally {
      setGifUploading((prev) => ({ ...prev, [state]: false }));
    }
  };

  const loadMapBgSettings = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setMapBgLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getMapBgSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMapBgImage(data.image || "");
        setMapBgApplyQuestion(!!data.applyQuestion);
        if (data.image) setMapBgPreview(`${process.env.REACT_APP_S3_PATH}${data.image}`);
      }
    } catch (e) {
      console.error("loadMapBgSettings error:", e);
    } finally {
      setMapBgLoading(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  const handleMapBgImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      Swal.fire("Error", "Only PNG images are allowed", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire("Error", "Image must be less than 10MB", "error");
      e.target.value = "";
      return;
    }
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== 1448 || img.height !== 1086) {
        Swal.fire({
          icon: "error",
          title: "Invalid Image Size",
          html: `Image must be exactly <strong>1448×1086 pixels</strong>.<br/>Your image is <strong>${img.width}×${img.height} pixels</strong>.<br/>Please resize and try again.`,
        });
        e.target.value = "";
        return;
      }
      setMapBgFile(file);
      setMapBgPreview(URL.createObjectURL(file));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      Swal.fire("Error", "Failed to load image. Please try another file.", "error");
      e.target.value = "";
    };
    img.src = objectUrl;
  };

  const handleSaveMapBg = async () => {
    if (!mapBgFile && !mapBgImage) {
      Swal.fire("Info", "Please select an image first", "info");
      return;
    }
    setMapBgUploading(true);
    try {
      let imageKey = null;
      if (mapBgFile) {
        imageKey = await uploadFileDirectToS3({
          adminToken,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          kind: "mapbg",
          file: mapBgFile,
        });
      }

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveMapBgSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          applyQuestion: mapBgApplyQuestion ? "1" : "0",
          ...(imageKey ? { image: imageKey } : {}),
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.image) {
          setMapBgImage(data.image);
          setMapBgPreview(`${process.env.REACT_APP_S3_PATH}${data.image}`);
          setMapBgFile(null);
          if (mapBgInputRef.current) mapBgInputRef.current.value = "";
        }
        Swal.fire({ icon: "success", title: "Saved!", text: "Map background settings saved.", timer: 1500, showConfirmButton: false });
      } else {
        throw new Error(data.message);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed", "error");
    } finally {
      setMapBgUploading(false);
    }
  };

  const handleRemoveMapBg = async () => {
    const result = await Swal.fire({
      title: "Remove background?",
      text: "This will remove the map background image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove it",
    });
    if (!result.isConfirmed) return;
    setMapBgUploading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveMapBgSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          applyQuestion: "0",
          removeImage: "1",
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMapBgImage("");
        setMapBgPreview("");
        setMapBgApplyQuestion(false);
        setMapBgFile(null);
        if (mapBgInputRef.current) mapBgInputRef.current.value = "";
        Swal.fire({ icon: "success", title: "Removed!", timer: 1200, showConfirmButton: false });
      } else {
        throw new Error(data.message);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Remove failed", "error");
    } finally {
      setMapBgUploading(false);
    }
  };

  const loadSecretMessageSettings = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setSecretLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getSecretMessageSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSecretEnabled(!!data.enabled);
        setSecretMessage(data.message || "");
      }
    } catch (e) {
      console.error("loadSecretMessageSettings error:", e);
    } finally {
      setSecretLoading(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  const handleSecretMessageChange = (e) => {
    // Letters and spaces only — no leading space, no double spaces. Spaces
    // are allowed for readability but don't count toward the 28 letters.
    const cleaned = e.target.value.replace(/[^A-Za-z ]/g, "").replace(/\s+/g, " ").replace(/^ /, "");
    setSecretMessage(cleaned);
  };

  const secretLetterCount = secretMessage.replace(/ /g, "").length;

  const handleSaveSecretMessage = async () => {
    if (secretEnabled && secretLetterCount !== 28) {
      Swal.fire("Info", "Secret message must contain exactly 28 letters (spaces don't count)", "info");
      return;
    }
    setSecretSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveSecretMessageSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          enabled: secretEnabled,
          message: secretMessage,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSecretEnabled(!!data.enabled);
        if (data.message) setSecretMessage(data.message);
        Swal.fire({ icon: "success", title: "Saved!", timer: 1500, showConfirmButton: false });
      } else {
        throw new Error(data.message);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed", "error");
    } finally {
      setSecretSaving(false);
    }
  };

  const loadSoundSettings = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setSoundLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getSoundSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSoundEnabled(!!data.enabled);
        setSoundName(data.sound || "");
        setSoundPreviewUrl(data.sound ? `${process.env.REACT_APP_S3_PATH}${data.sound}` : "");
      }
    } catch (e) {
      console.error("loadSoundSettings error:", e);
    } finally {
      setSoundLoading(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  const handleSoundFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)) {
      Swal.fire("Error", "Only MP3, WAV, OGG, M4A or AAC audio files are allowed", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire("Error", "Sound file must be less than 10MB", "error");
      e.target.value = "";
      return;
    }
    setSoundFile(file);
    setSoundPreviewUrl(URL.createObjectURL(file));
  };

  const handleSaveSound = async () => {
    if (!soundFile && !soundName) {
      Swal.fire("Info", "Please select a sound file first", "info");
      return;
    }
    setSoundUploading(true);
    try {
      let soundKey = null;
      if (soundFile) {
        soundKey = await uploadFileDirectToS3({
          adminToken,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          kind: "sound",
          file: soundFile,
        });
      }

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveSoundSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          enabled: soundEnabled ? "1" : "0",
          ...(soundKey ? { sound: soundKey } : {}),
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.sound) {
          setSoundName(data.sound);
          setSoundPreviewUrl(`${process.env.REACT_APP_S3_PATH}${data.sound}`);
          setSoundFile(null);
          if (soundInputRef.current) soundInputRef.current.value = "";
        }
        Swal.fire({ icon: "success", title: "Saved!", text: "Background sound settings saved.", timer: 1500, showConfirmButton: false });
      } else {
        throw new Error(data.message);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed", "error");
    } finally {
      setSoundUploading(false);
    }
  };

  const handleRemoveSound = async () => {
    const result = await Swal.fire({
      title: "Remove background sound?",
      text: "This will remove the background music from the game.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove it",
    });
    if (!result.isConfirmed) return;
    setSoundUploading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveSoundSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          removeSound: "1",
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSoundEnabled(false);
        setSoundName("");
        setSoundPreviewUrl("");
        setSoundFile(null);
        if (soundInputRef.current) soundInputRef.current.value = "";
        Swal.fire({ icon: "success", title: "Removed!", timer: 1200, showConfirmButton: false });
      } else {
        throw new Error(data.message);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Remove failed", "error");
    } finally {
      setSoundUploading(false);
    }
  };

  const loadQuestions = async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get28StatesQuestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions || []);
      } else {
        throw new Error(data.message || "Failed to load questions");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to load questions", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionsPerStateSettings = async () => {
    if (!currentTheme || !adminToken) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getQuestionsPerStateSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const data = await res.json();
      if (data.success && data.count) {
        setQuestionsPerStateCount(data.count);
      }
    } catch (error) {
      console.error("loadQuestionsPerStateSettings error:", error);
    }
  };

  const handleSelectQuestionsPerState = async (count) => {
    if (count === questionsPerStateCount || questionsPerStateSaving) return;
    setQuestionsPerStateSaving(true);
    const previous = questionsPerStateCount;
    setQuestionsPerStateCount(count); // optimistic — reverted below on failure
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveQuestionsPerStateSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          count,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to save");
      // The question list/summary below is capped server-side to this count.
      await loadQuestions();
    } catch (error) {
      setQuestionsPerStateCount(previous);
      Swal.fire("Error", error.message || "Failed to update questions-per-state count", "error");
    } finally {
      setQuestionsPerStateSaving(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      Swal.fire("Error", "Only PNG images are allowed", "error");
      e.target.value = "";
      return;
    }

    if (file.type !== "image/png") {
      Swal.fire("Error", "Only PNG images are allowed", "error");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Error", "Image must be less than 5MB", "error");
      e.target.value = "";
      return;
    }

    // Validate image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      if (img.width !== 534 || img.height !== 534) {
        Swal.fire({
          icon: "error",
          title: "Invalid Image Size",
          html: `Image must be exactly <strong>534×534 pixels</strong>.<br/>Your image is <strong>${img.width}×${img.height} pixels</strong>.<br/>Please resize and try again.`,
        });
        e.target.value = "";
        return;
      }

      // Image is valid
      setFormData((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      Swal.fire("Error", "Failed to load image. Please try another file.", "error");
      e.target.value = "";
    };

    img.src = objectUrl;
  };

  const validateForm = () => {
    // Character limit validations
    if (!formData.question_title.trim()) {
      Swal.fire("Validation", "Question title is required", "warning");
      return false;
    }
    if (formData.question_title.length > 50) {
      Swal.fire("Validation", "Question title must not exceed 50 characters", "warning");
      return false;
    }

    if (!formData.question.trim()) {
      Swal.fire("Validation", "Question text is required", "warning");
      return false;
    }
    if (formData.question.length > 200) {
      Swal.fire("Validation", "Question text must not exceed 200 characters", "warning");
      return false;
    }

    if (!formData.correct_answer.trim()) {
      Swal.fire("Validation", "Correct answer is required", "warning");
      return false;
    }
    if (formData.correct_answer.length > 50) {
      Swal.fire("Validation", "Correct answer must not exceed 50 characters", "warning");
      return false;
    }

    if (formData.did_you_know.length > 200) {
      Swal.fire("Validation", "Did you know must not exceed 200 characters", "warning");
      return false;
    }

    if (!formData.state) {
      Swal.fire("Validation", "Please select a state", "warning");
      return false;
    }
    
    // Validate based on question type
    if (formData.type_question === "radioselect") {
      if (!formData.option_one.trim() || !formData.option_two.trim() || !formData.option_three.trim()) {
        Swal.fire("Validation", "All three options are required for multiple choice questions", "warning");
        return false;
      }
      if (formData.option_one.length > 50) {
        Swal.fire("Validation", "Option 1 must not exceed 50 characters", "warning");
        return false;
      }
      if (formData.option_two.length > 50) {
        Swal.fire("Validation", "Option 2 must not exceed 50 characters", "warning");
        return false;
      }
      if (formData.option_three.length > 50) {
        Swal.fire("Validation", "Option 3 must not exceed 50 characters", "warning");
        return false;
      }
    } else if (formData.type_question === "truefalse") {
      if (!formData.option_one.trim() || !formData.option_two.trim()) {
        Swal.fire("Validation", "Both True and False options are required", "warning");
        return false;
      }
      // Validate that options are TRUE/FALSE
      const opt1 = formData.option_one.trim().toUpperCase();
      const opt2 = formData.option_two.trim().toUpperCase();
      if ((opt1 !== "TRUE" && opt1 !== "FALSE") || (opt2 !== "TRUE" && opt2 !== "FALSE")) {
        Swal.fire("Validation", "Options must be TRUE or FALSE for true/false questions", "warning");
        return false;
      }
      if (opt1 === opt2) {
        Swal.fire("Validation", "Options must be different (one TRUE, one FALSE)", "warning");
        return false;
      }
      const correctUpper = formData.correct_answer.trim().toUpperCase();
      if (correctUpper !== "TRUE" && correctUpper !== "FALSE") {
        Swal.fire("Validation", "Correct answer must be TRUE or FALSE for true/false questions", "warning");
        return false;
      }
    }

    // Validate answer_id
    if (formData.answer_id) {
      const answerIdNum = Number(formData.answer_id);
      if (isNaN(answerIdNum) || !Number.isInteger(answerIdNum)) {
        Swal.fire("Validation", "Answer ID must be a valid number", "warning");
        return false;
      }

      // Validate that answer_id matches the correct answer
      if (formData.type_question === "radioselect") {
        const options = [formData.option_one, formData.option_two, formData.option_three];
        const answerIndex = answerIdNum - 1; // Convert 1-based to 0-based index
        if (answerIndex < 0 || answerIndex >= options.length) {
          Swal.fire("Validation", "Answer ID must be between 1 and 3 for multiple choice questions", "warning");
          return false;
        }
        if (options[answerIndex].trim() !== formData.correct_answer.trim()) {
          Swal.fire("Validation", `Answer ID ${answerIdNum} does not match the correct answer. Option ${answerIdNum} is "${options[answerIndex]}" but correct answer is "${formData.correct_answer}"`, "warning");
          return false;
        }
      } else if (formData.type_question === "truefalse") {
        const options = [formData.option_one, formData.option_two];
        const answerIndex = answerIdNum - 1;
        if (answerIndex < 0 || answerIndex >= options.length) {
          Swal.fire("Validation", "Answer ID must be 1 or 2 for true/false questions", "warning");
          return false;
        }
        if (options[answerIndex].trim().toUpperCase() !== formData.correct_answer.trim().toUpperCase()) {
          Swal.fire("Validation", `Answer ID ${answerIdNum} does not match the correct answer. Option ${answerIdNum} is "${options[answerIndex]}" but correct answer is "${formData.correct_answer}"`, "warning");
          return false;
        }
      }
    }

    // Validate questions per state
    const stateQuestions = questions.filter(
      (q) => q.state === formData.state && q.id !== editingQuestion?.id
    );
    if (stateQuestions.length >= 5) {
      Swal.fire("Validation", `Maximum 5 questions per state. ${formData.state} already has ${stateQuestions.length} questions.`, "warning");
      return false;
    }

    return true;
  };

  const handleSaveQuestion = async () => {
    if (!validateForm()) return;

    const finalFormData = formData.type_question === "typetext"
      ? { ...formData, answer_id: "1" }
      : formData;

    const updatedQuestions = editingQuestion
      ? questions.map((q) => (q.id === editingQuestion.id ? { ...finalFormData, id: q.id } : q))
      : [...questions, { ...finalFormData, id: Date.now(), sort_order: questions.length + 1 }];

    await saveQuestionsToBackend(updatedQuestions);
  };

  const saveQuestionsToBackend = async (questionsToSave) => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("organizationId", organizationId || "admin");
      formDataToSend.append("sessionId", sessionId || "admin");
      formDataToSend.append("currentTheme", currentTheme);
      formDataToSend.append("questions", JSON.stringify(questionsToSave.map((q) => ({
        id: q.id,
        sort_order: q.sort_order,
        question_title: q.question_title,
        question: q.question,
        image: typeof q.image === "string" ? q.image : "",
        option_one: q.option_one,
        option_two: q.option_two,
        option_three: q.option_three,
        correct_answer: q.correct_answer,
        answer_id: q.answer_id,
        did_you_know: q.did_you_know,
        type_question: q.type_question,
        state: q.state,
      }))));

      // Append image files
      questionsToSave.forEach((q, index) => {
        if (q.image && typeof q.image !== "string") {
          formDataToSend.append(`image_${index}`, q.image);
        }
      });

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save28StatesQuestions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        body: formDataToSend,
      });

      const data = await res.json();
      if (data.success) {
        Swal.fire("Success", "Questions saved successfully", "success");
        setShowAddForm(false);
        setEditingQuestion(null);
        setFormData(emptyQuestion());
        await loadQuestions();
      } else {
        throw new Error(data.message || "Failed to save questions");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to save questions", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This question will be deleted permanently",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      const updatedQuestions = questions.filter((q) => q.id !== questionId);
      await saveQuestionsToBackend(updatedQuestions);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setFormData({
      ...question,
      image: null,
      imagePreview: question.image ? `${process.env.REACT_APP_S3_PATH}${question.image}` : "",
      answer_id: question.type_question === "typetext" ? "1" : question.answer_id,
    });
    setShowAddForm(true);
  };

  const resetCsvSelection = () => {
    setCsvFile(null);
    setCsvFileName("");
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Error", "Only CSV files allowed", "error").then(() => {
        // Clear the stale file reference only once the admin has acknowledged the
        // popup, so re-picking a file afterwards never resends an old File handle
        // (which is what triggers net::ERR_UPLOAD_FILE_CHANGED).
        resetCsvSelection();
      });
      return;
    }

    setCsvFile(file);
    setCsvFileName(file.name);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      Swal.fire("Info", "Please select a CSV file first", "info");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");
      formData.append("currentTheme", currentTheme);
      formData.append("csvFile", csvFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/import28StatesQuestionsCSV`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        const shortStates = Array.isArray(data.shortStates) ? data.shortStates : [];
        Swal.fire({
          icon: shortStates.length > 0 ? "warning" : "success",
          title: shortStates.length > 0 ? "Imported — But Check This" : "Import Successful!",
          html: `
            <p>${data.message || "CSV imported successfully"}</p>
            <p>Total questions imported: <strong>${data.count || 0}</strong></p>
            ${shortStates.length > 0 ? `
              <p style="margin-top:10px;">The current setting needs <strong>${data.requiredCount}</strong> questions per state.
              These states have fewer than that:</p>
              <p style="font-weight:600;">${shortStates.join(", ")}</p>
            ` : ""}
          `,
        });
        setShowCsvModal(false);
        resetCsvSelection();
        await loadQuestions();
      } else {
        throw new Error(data.message || "Failed to import CSV");
      }
    } catch (error) {
      // Reset only after the admin acknowledges the popup, so the next file pick
      // always starts from a clean input instead of resending a stale File handle
      // (root cause of net::ERR_UPLOAD_FILE_CHANGED on a second bad upload).
      Swal.fire("Please check excel", error.message || "Failed to import CSV", "error").then(() => {
        resetCsvSelection();
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (questions.length === 0) {
      Swal.fire("Info", "No questions to export", "info");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/export28StatesQuestionsCSV`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to export CSV");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "28states_questions.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      Swal.fire("Success", "CSV exported successfully", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to export CSV", "error");
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const headers = [
      "question_title",
      "question",
      "image",
      "option_one",
      "option_two",
      "option_three",
      "correct_answer",
      "answer_id",
      "did_you_know",
      "type_question",
      "state",
    ];
    const sampleData = [
      [
        "The essence of Incredible INDIA",
        "Nagarjuna Sagar Dam is built on which of the following rivers?",
        "",
        "Krishna",
        "Narmada",
        "Ganga",
        "Krishna",
        "1",
        "",
        "radioselect",
        "ANDHRA PRADESH",
      ],
      [
        "The essence of Incredible INDIA",
        "Andhra Pradesh is known as the Egg Bowl of Asia.",
        "",
        "TRUE",
        "FALSE",
        "",
        "TRUE",
        "1",
        "Andhra Pradesh is the largest egg producer in India",
        "truefalse",
        "ANDHRA PRADESH",
      ],
      [
        "INDIA'S Sunrise State",
        "In which city the largest monastery of India is located?",
        "",
        "",
        "",
        "",
        "Tawang",
        "1",
        "",
        "typetext",
        "ARUNACHAL PRADESH",
      ],
    ];

    const csvContent = [headers.join(","), ...sampleData.map((row) => row.map(cell => `"${cell}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "28states_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const groupedQuestions = STATES_LIST.reduce((acc, state) => {
    acc[state] = questions.filter((q) => q.state === state);
    return acc;
  }, {});

  const missingStatesForCount = STATES_LIST
    .map((state) => ({ state, count: groupedQuestions[state]?.length || 0 }))
    .filter(({ count }) => count < questionsPerStateCount);

  const totalQuestionPages = Math.max(1, Math.ceil(questions.length / QUESTIONS_PAGE_SIZE));
  const safeQuestionsPage = Math.min(questionsPage, totalQuestionPages);
  const paginatedQuestions = questions.slice(
    (safeQuestionsPage - 1) * QUESTIONS_PAGE_SIZE,
    safeQuestionsPage * QUESTIONS_PAGE_SIZE
  );

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="themeupdate-panel-wide">
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">
            <span>28 States Game Management</span>
            <button type="button" className="themeupdate-tour-btn" onClick={startTour}>
              <i className="fa-solid fa-circle-info"></i> Take a Tour
            </button>
          </div>

          {/* Tab Switcher */}
          <div className="themeupdate-tabs">
            <button
              className={`themeupdate-tab ${activeTab === "questions" ? "active" : ""}`}
              onClick={() => setActiveTab("questions")}
            >
              <i className="fa-solid fa-circle-question"></i> Questions
            </button>
            <button
              className={`themeupdate-tab ${activeTab === "gifs" ? "active" : ""}`}
              onClick={() => setActiveTab("gifs")}
            >
              <i className="fa-solid fa-film"></i> State GIFs
            </button>
            <button
              className={`themeupdate-tab ${activeTab === "mapbg" ? "active" : ""}`}
              onClick={() => setActiveTab("mapbg")}
            >
              <i className="fa-solid fa-mountain-sun"></i> Map Background
            </button>
            <button
              className={`themeupdate-tab ${activeTab === "secret" ? "active" : ""}`}
              onClick={() => setActiveTab("secret")}
            >
              <i className="fa-solid fa-key"></i> Secret Message
            </button>
            <button
              className={`themeupdate-tab ${activeTab === "sound" ? "active" : ""}`}
              onClick={() => setActiveTab("sound")}
            >
              <i className="fa-solid fa-music"></i> Background Sound
            </button>
          </div>

          {/* ── STATE GIFs TAB ── */}
          {activeTab === "gifs" && (
            <div className="themeupdate-gifs-section">
              <p className="themeupdate-gifs-hint">
                Upload a GIF for each state. When a player clicks that state on the map, the GIF will play as an overlay animation.
                <br />Max 5MB per GIF.
              </p>
              {gifLoading ? (
                <div className="themeupdate-loading">Loading GIFs...</div>
              ) : (
                <div className="themeupdate-gifs-grid">
                  {STATES_LIST.map((state) => (
                    <div key={state} className="themeupdate-gif-card">
                      <div className="themeupdate-gif-state-name">{state}</div>
                      {stateGifs[state] ? (
                        <div className="themeupdate-gif-preview">
                          <img
                            src={`${process.env.REACT_APP_S3_PATH}${stateGifs[state]}`}
                            alt={`${state} GIF`}
                          />
                          <span className="themeupdate-gif-badge">
                            <i className="fa-solid fa-check"></i> GIF Set
                          </span>
                        </div>
                      ) : (
                        <div className="themeupdate-gif-empty">
                          <i className="fa-solid fa-image"></i>
                          <span>No GIF</span>
                        </div>
                      )}
                      <label className={`themeupdate-gif-upload-btn ${gifUploading[state] ? "uploading" : ""}`}>
                        {gifUploading[state] ? (
                          <><i className="fa-solid fa-spinner fa-spin"></i> Uploading…</>
                        ) : (
                          <><i className="fa-solid fa-upload"></i> {stateGifs[state] ? "Replace GIF" : "Upload GIF"}</>
                        )}
                        <input
                          type="file"
                          accept=".gif,image/gif"
                          style={{ display: "none" }}
                          disabled={!!gifUploading[state]}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleGifUpload(state, file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── MAP BACKGROUND TAB ── */}
          {activeTab === "mapbg" && (
            <div className="themeupdate-gifs-section">
              <p className="themeupdate-gifs-hint">
                Upload a background image displayed inside the map card on the game screen.<br />
                Required size: <strong>1448 × 1086 pixels</strong>, PNG only, max 10 MB.
              </p>

              {mapBgLoading ? (
                <div className="themeupdate-loading">Loading settings…</div>
              ) : (
                <div style={{ maxWidth: 560 }}>

                  {/* Current preview */}
                  {mapBgPreview && (
                    <div style={{ marginBottom: 20 }}>
                      <p style={{ fontWeight: 600, marginBottom: 8, color: "#374151" }}>Current Background:</p>
                      <div style={{ position: "relative", display: "inline-block" }}>
                        <img
                          src={mapBgPreview}
                          alt="Map background preview"
                          style={{ width: "100%", maxWidth: 440, height: "auto", borderRadius: 12, border: "2px solid #e5e7eb", display: "block" }}
                        />
                        {mapBgImage && (
                          <span style={{
                            position: "absolute", top: 8, right: 8,
                            background: "#22c55e", color: "#fff",
                            fontSize: 11, fontWeight: 700, padding: "3px 10px",
                            borderRadius: 20,
                          }}>
                            <i className="fa-solid fa-check"></i> Active
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Upload area */}
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontWeight: 600, marginBottom: 8, color: "#374151" }}>
                      {mapBgImage ? "Replace Image:" : "Upload Image:"}
                    </p>
                    <input
                      ref={mapBgInputRef}
                      type="file"
                      accept=".png,image/png"
                      onChange={handleMapBgImageChange}
                      className="themeupdate-file-input"
                      id="mapbg-upload-input"
                    />
                    <label htmlFor="mapbg-upload-input" className="themeupdate-file-label-large" style={{ maxWidth: 320 }}>
                      <i className="fa-solid fa-image"></i>
                      <span>{mapBgFile ? mapBgFile.name : "Choose PNG (1448×1086)"}</span>
                    </label>
                    <div className="themeupdate-hint themeupdate-hint-image" style={{ marginTop: 6 }}>
                      <i className="fa-solid fa-info-circle"></i> PNG only · exactly 1448×1086 px · max 10 MB
                    </div>
                  </div>

                  {/* Toggle */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "#f9fafb", border: "1.5px solid #e5e7eb",
                    borderRadius: 12, padding: "14px 18px", marginBottom: 24,
                  }}>
                    <label className="themeupdate-mapbg-toggle" style={{ position: "relative", display: "inline-block", width: 44, height: 24, flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={mapBgApplyQuestion}
                        onChange={(e) => setMapBgApplyQuestion(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: "absolute", cursor: "pointer", inset: 0,
                        background: mapBgApplyQuestion ? "#7c3aed" : "#d1d5db",
                        borderRadius: 999, transition: "background 0.25s",
                      }}>
                        <span style={{
                          position: "absolute", top: 3, left: mapBgApplyQuestion ? 23 : 3,
                          width: 18, height: 18, borderRadius: "50%",
                          background: "#fff", transition: "left 0.25s",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                        }} />
                      </span>
                    </label>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, color: "#1f2937", fontSize: 14 }}>
                        Apply background to question panel
                      </p>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
                        When ON, the same image also appears behind the question/answer card. Default: OFF.
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="themeupdate-form-actions" style={{ justifyContent: "flex-start", gap: 12 }}>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveMapBg}
                      disabled={mapBgUploading || (!mapBgFile && !mapBgImage)}
                    >
                      {mapBgUploading ? (
                        <><i className="fa-solid fa-spinner fa-spin"></i> Saving…</>
                      ) : (
                        <><i className="fa-solid fa-save"></i> Save Settings</>
                      )}
                    </button>
                    {mapBgImage && (
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-outline"
                        onClick={handleRemoveMapBg}
                        disabled={mapBgUploading}
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                      >
                        <i className="fa-solid fa-trash"></i> Remove Background
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SECRET MESSAGE TAB ── */}
          {activeTab === "secret" && (
            <div className="themeupdate-gifs-section">
              <p className="themeupdate-gifs-hint">
                When enabled, players uncover one random letter of this message for every state they
                complete. Completing all 28 states reveals the full message.
              </p>

              {secretLoading ? (
                <div className="themeupdate-loading">Loading settings…</div>
              ) : (
                <div style={{ maxWidth: 560 }}>
                  {/* Toggle */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "#f9fafb", border: "1.5px solid #e5e7eb",
                    borderRadius: 12, padding: "14px 18px", marginBottom: 24,
                  }}>
                    <label className="themeupdate-mapbg-toggle" style={{ position: "relative", display: "inline-block", width: 44, height: 24, flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={secretEnabled}
                        onChange={(e) => setSecretEnabled(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: "absolute", cursor: "pointer", inset: 0,
                        background: secretEnabled ? "#7c3aed" : "#d1d5db",
                        borderRadius: 999, transition: "background 0.25s",
                      }}>
                        <span style={{
                          position: "absolute", top: 3, left: secretEnabled ? 23 : 3,
                          width: 18, height: 18, borderRadius: "50%",
                          background: "#fff", transition: "left 0.25s",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                        }} />
                      </span>
                    </label>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, color: "#1f2937", fontSize: 14 }}>
                        Secret Message
                      </p>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
                        Turn on to reveal a hidden 28-letter message as states are completed. Default: OFF.
                      </p>
                    </div>
                  </div>

                  {secretEnabled && (
                    <div style={{ marginBottom: 24 }}>
                      <p style={{ fontWeight: 600, marginBottom: 8, color: "#374151" }}>
                        Secret Message (exactly 28 letters — spaces between words are allowed):
                      </p>
                      <input
                        type="text"
                        value={secretMessage}
                        onChange={handleSecretMessageChange}
                        placeholder="e.g. INDIA IS INCREDIBLE AND DIVERSED"
                        className="themeupdate-file-input"
                        style={{
                          display: "block", width: "100%", padding: "10px 12px",
                          border: "1.5px solid #e5e7eb", borderRadius: 8,
                          fontSize: 15, letterSpacing: 1, textTransform: "uppercase",
                        }}
                      />
                      <div
                        className="themeupdate-hint themeupdate-hint-image"
                        style={{ marginTop: 6, color: secretLetterCount === 28 ? "#16a34a" : "#dc2626" }}
                      >
                        <i className="fa-solid fa-info-circle"></i> {secretLetterCount}/28 letters
                        {secretLetterCount === 28 ? " — looks good" : " — spaces are free, only letters count"}
                      </div>
                    </div>
                  )}

                  <div className="themeupdate-form-actions" style={{ justifyContent: "flex-start", gap: 12 }}>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveSecretMessage}
                      disabled={secretSaving || (secretEnabled && secretLetterCount !== 28)}
                    >
                      {secretSaving ? (
                        <><i className="fa-solid fa-spinner fa-spin"></i> Saving…</>
                      ) : (
                        <><i className="fa-solid fa-save"></i> Save Settings</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── BACKGROUND SOUND TAB ── */}
          {activeTab === "sound" && (
            <div className="themeupdate-gifs-section">
              <p className="themeupdate-gifs-hint">
                Upload background music/sound for the game screen. Max file size: <strong>10MB</strong>.
                Supported formats: MP3, WAV, OGG, M4A, AAC.
              </p>

              {soundLoading ? (
                <div className="themeupdate-loading">Loading settings…</div>
              ) : (
                <div style={{ maxWidth: 560 }}>
                  {soundPreviewUrl && (
                    <div style={{ marginBottom: 20 }}>
                      <p style={{ fontWeight: 600, marginBottom: 8, color: "#374151" }}>Current Sound:</p>
                      <audio src={soundPreviewUrl} controls style={{ width: "100%" }} />
                    </div>
                  )}

                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontWeight: 600, marginBottom: 8, color: "#374151" }}>
                      {soundName ? "Replace Sound:" : "Upload Sound:"}
                    </p>
                    <input
                      ref={soundInputRef}
                      type="file"
                      accept=".mp3,.wav,.ogg,.m4a,.aac,audio/*"
                      onChange={handleSoundFileChange}
                      className="themeupdate-file-input"
                      id="sound-upload-input"
                    />
                    <label htmlFor="sound-upload-input" className="themeupdate-file-label-large" style={{ maxWidth: 320 }}>
                      <i className="fa-solid fa-music"></i>
                      <span>{soundFile ? soundFile.name : "Choose Audio File"}</span>
                    </label>
                    <div className="themeupdate-hint themeupdate-hint-image" style={{ marginTop: 6 }}>
                      <i className="fa-solid fa-info-circle"></i> MP3 / WAV / OGG / M4A / AAC · max 10 MB
                    </div>
                  </div>

                  <div style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "#f9fafb", border: "1.5px solid #e5e7eb",
                    borderRadius: 12, padding: "14px 18px", marginBottom: 24,
                  }}>
                    <label className="themeupdate-mapbg-toggle" style={{ position: "relative", display: "inline-block", width: 44, height: 24, flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={soundEnabled}
                        onChange={(e) => setSoundEnabled(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: "absolute", cursor: "pointer", inset: 0,
                        background: soundEnabled ? "#7c3aed" : "#d1d5db",
                        borderRadius: 999, transition: "background 0.25s",
                      }}>
                        <span style={{
                          position: "absolute", top: 3, left: soundEnabled ? 23 : 3,
                          width: 18, height: 18, borderRadius: "50%",
                          background: "#fff", transition: "left 0.25s",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                        }} />
                      </span>
                    </label>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, color: "#1f2937", fontSize: 14 }}>
                        Play Background Sound
                      </p>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
                        When ON, this sound auto-plays on the game screen with a mute/unmute speaker icon. Default: OFF.
                      </p>
                    </div>
                  </div>

                  <div className="themeupdate-form-actions" style={{ justifyContent: "flex-start", gap: 12 }}>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveSound}
                      disabled={soundUploading || (!soundFile && !soundName)}
                    >
                      {soundUploading ? (
                        <><i className="fa-solid fa-spinner fa-spin"></i> Saving…</>
                      ) : (
                        <><i className="fa-solid fa-save"></i> Save Settings</>
                      )}
                    </button>
                    {soundName && (
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-outline"
                        onClick={handleRemoveSound}
                        disabled={soundUploading}
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                      >
                        <i className="fa-solid fa-trash"></i> Remove Sound
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── QUESTIONS TAB ── */}
          {activeTab === "questions" && <>
          {/* Questions per state count */}
          <div className="themeupdate-qps-row">
            <span className="themeupdate-qps-label">Questions per state:</span>
            <div className="themeupdate-qps-tabs">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`themeupdate-qps-tab ${questionsPerStateCount === n ? "active" : ""}`}
                  onClick={() => handleSelectQuestionsPerState(n)}
                  disabled={questionsPerStateSaving}
                >
                  {n}
                </button>
              ))}
            </div>
            <span className="themeupdate-qps-hint">
              Only the first {questionsPerStateCount} question{questionsPerStateCount === 1 ? "" : "s"} authored for
              each state (by order) are used in the game, the list below, and CSV export.
            </span>
          </div>

          {/* States missing questions for the selected count */}
          {questions.length > 0 && (
            missingStatesForCount.length > 0 ? (
              <div className="themeupdate-missing-banner">
                <div className="themeupdate-missing-banner-title">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  {missingStatesForCount.length} state{missingStatesForCount.length === 1 ? "" : "s"} short of {questionsPerStateCount} question{questionsPerStateCount === 1 ? "" : "s"}:
                </div>
                <div className="themeupdate-missing-banner-list">
                  {missingStatesForCount.map(({ state, count }) => (
                    <span key={state} className="themeupdate-missing-chip">
                      {state} ({count}/{questionsPerStateCount})
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="themeupdate-missing-banner themeupdate-missing-banner--ok">
                <i className="fa-solid fa-circle-check"></i> All 28 states have at least {questionsPerStateCount} question{questionsPerStateCount === 1 ? "" : "s"}.
              </div>
            )
          )}

          {/* Action Buttons */}
          <div className="themeupdate-actions-row">
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-success"
              onClick={() => {
                setShowAddForm(true);
                setEditingQuestion(null);
                setFormData(emptyQuestion());
              }}
              disabled={loading}
            >
              <i className="fa-solid fa-plus"></i> Add Question
            </button>
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-primary"
              onClick={() => setShowCsvModal(true)}
              disabled={loading}
            >
              <i className="fa-solid fa-upload"></i> Upload CSV
            </button>
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-primary"
              onClick={handleExportCsv}
              disabled={loading || questions.length === 0}
            >
              <i className="fa-solid fa-download"></i> Export CSV
            </button>
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-outline"
              onClick={downloadSampleCsv}
            >
              <i className="fa-solid fa-file-csv"></i> Sample CSV
            </button>
          </div>

          {/* CSV Upload Modal */}
          {showCsvModal && (
            <div className="themeupdate-modal-overlay">
              <div className="themeupdate-modal themeupdate-modal-csv" onClick={(e) => e.stopPropagation()}>
                <div className="themeupdate-modal-header">
                  <h3 className="themeupdate-modal-title">Upload CSV File</h3>
                  <button
                    type="button"
                    className="themeupdate-modal-close"
                    onClick={() => {
                      setShowCsvModal(false);
                      resetCsvSelection();
                    }}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>

                <div className="themeupdate-modal-body">
                  <div className="themeupdate-csv-upload-container">
                    <div className="themeupdate-csv-warning">
                      <i className="fa-solid fa-exclamation-triangle"></i>
                      <div>
                        <strong>Warning:</strong> This will DELETE all existing questions and replace them with the questions from the CSV file.
                        <br />
                        Current questions: <strong>{questions.length}</strong>
                      </div>
                    </div>

                    <div className="themeupdate-csv-upload-area">
                      <input
                        ref={csvInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCsvFileSelect}
                        className="themeupdate-file-input"
                        id="csv-upload-input-modal"
                      />
                      <label htmlFor="csv-upload-input-modal" className="themeupdate-file-label-large">
                        <i className="fa-solid fa-file-csv"></i>
                        <span>{csvFileName || "Choose CSV File"}</span>
                      </label>
                    </div>

                    <div className="themeupdate-csv-instructions">
                      <h4>CSV File Requirements:</h4>
                      <ul>
                        <li>CSV must have headers: <code>question_title, question, image, option_one, option_two, option_three, correct_answer, answer_id, did_you_know, type_question, state</code></li>
                        <li>No need for <code>id</code> or <code>sort_order</code> - they are auto-generated</li>
                        <li>Each state must have 1-5 questions</li>
                        <li>type_question must be exactly one of: "radioselect" (MCQ, 3 options), "truefalse" (T/F, 2 options: TRUE/FALSE), or "typetext" (TEXT, no options) - any other value will be rejected</li>
                        <li>For true/false: option_one and option_two must be TRUE and FALSE</li>
                        <li>Image: PNG only, 534×534 pixels (optional, can leave empty in CSV)</li>
                        <li>Character limits: question_title (50), question (200), options (50 each), correct_answer (50), did_you_know (200)</li>
                        <li>answer_id must be a number matching the correct option (for type_question "typetext", answer_id is always fixed to 1 and any other value is ignored)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="themeupdate-form-actions">
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-primary"
                      onClick={handleCsvUpload}
                      disabled={!csvFile || loading}
                    >
                      {loading ? "Uploading..." : "Upload & Replace All Questions"}
                    </button>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-outline"
                      onClick={() => {
                        setShowCsvModal(false);
                        resetCsvSelection();
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Form Modal */}
          {showAddForm && (
            <div className="themeupdate-modal-overlay">
              <div className="themeupdate-modal" onClick={(e) => e.stopPropagation()}>
                <div className="themeupdate-modal-header">
                  <h3 className="themeupdate-modal-title">
                    {editingQuestion ? "Edit Question" : "Add New Question"}
                  </h3>
                  <button
                    type="button"
                    className="themeupdate-modal-close"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingQuestion(null);
                      setFormData(emptyQuestion());
                    }}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>

                <div className="themeupdate-modal-body">
                  <div className="themeupdate-form-grid">
                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Question Title * (max 50 chars)</label>
                  <input
                    type="text"
                    className="themeupdate-input"
                    value={formData.question_title}
                    onChange={(e) => handleFormChange("question_title", e.target.value)}
                    placeholder="e.g., The essence of Incredible INDIA"
                    maxLength={50}
                  />
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">State *</label>
                  <select
                    className="themeupdate-select"
                    value={formData.state}
                    onChange={(e) => handleFormChange("state", e.target.value)}
                  >
                    <option value="">Select State</option>
                    {STATES_LIST.map((state) => (
                      <option key={state} value={state}>
                        {state} ({groupedQuestions[state]?.length || 0}/5)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <label className="themeupdate-label">Question Text * (max 200 chars)</label>
                  <textarea
                    className="themeupdate-input themeupdate-textarea"
                    value={formData.question}
                    onChange={(e) => handleFormChange("question", e.target.value)}
                    placeholder="Enter the question text"
                    rows={3}
                    maxLength={200}
                  />
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Question Type</label>
                  <select
                    className="themeupdate-select"
                    value={formData.type_question}
                    onChange={(e) => {
                      const newType = e.target.value;
                      handleFormChange("type_question", newType);
                      // Clear and set appropriate options based on type
                      if (newType === "truefalse") {
                        handleFormChange("option_one", "TRUE");
                        handleFormChange("option_two", "FALSE");
                        handleFormChange("option_three", "");
                        handleFormChange("correct_answer", "");
                      } else if (newType === "typetext") {
                        handleFormChange("option_one", "");
                        handleFormChange("option_two", "");
                        handleFormChange("option_three", "");
                        handleFormChange("answer_id", "1");
                      } else {
                        // radioselect - clear all options
                        if (formData.option_one === "TRUE" || formData.option_one === "FALSE") {
                          handleFormChange("option_one", "");
                          handleFormChange("option_two", "");
                          handleFormChange("option_three", "");
                        }
                      }
                    }}
                  >
                    {QUESTION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Image (Optional)</label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept=".png,image/png"
                    onChange={handleImageChange}
                    className="themeupdate-file-input"
                    id="question-image-input"
                  />
                  <label htmlFor="question-image-input" className="themeupdate-file-label-small">
                    <i className="fa-solid fa-image"></i> Choose PNG Image
                  </label>
                  <div className="themeupdate-hint themeupdate-hint-image">
                    <i className="fa-solid fa-info-circle"></i> PNG only, exactly 534×534 pixels
                  </div>
                  {formData.imagePreview && (
                    <div className="themeupdate-image-preview">
                      <img src={formData.imagePreview} alt="Preview" />
                      <div className="themeupdate-image-preview-info">
                        <i className="fa-solid fa-check-circle"></i> 534×534 PNG
                      </div>
                    </div>
                  )}
                </div>

                {formData.type_question === "radioselect" && (
                  <>
                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Option 1 * (max 50 chars)</label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={formData.option_one}
                        onChange={(e) => handleFormChange("option_one", e.target.value)}
                        placeholder="First option"
                        maxLength={50}
                      />
                    </div>

                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Option 2 * (max 50 chars)</label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={formData.option_two}
                        onChange={(e) => handleFormChange("option_two", e.target.value)}
                        placeholder="Second option"
                        maxLength={50}
                      />
                    </div>

                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Option 3 * (max 50 chars)</label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={formData.option_three}
                        onChange={(e) => handleFormChange("option_three", e.target.value)}
                        placeholder="Third option"
                        maxLength={50}
                      />
                    </div>
                  </>
                )}

                {formData.type_question === "truefalse" && (
                  <>
                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Option 1 (TRUE/FALSE) *</label>
                      <select
                        className="themeupdate-select"
                        value={formData.option_one}
                        onChange={(e) => handleFormChange("option_one", e.target.value)}
                      >
                        <option value="TRUE">TRUE</option>
                        <option value="FALSE">FALSE</option>
                      </select>
                    </div>

                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Option 2 (TRUE/FALSE) *</label>
                      <select
                        className="themeupdate-select"
                        value={formData.option_two}
                        onChange={(e) => handleFormChange("option_two", e.target.value)}
                      >
                        <option value="FALSE">FALSE</option>
                        <option value="TRUE">TRUE</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Correct Answer * (max 50 chars)</label>
                  {formData.type_question === "truefalse" ? (
                    <select
                      className="themeupdate-select"
                      value={formData.correct_answer}
                      onChange={(e) => handleFormChange("correct_answer", e.target.value)}
                    >
                      <option value="">Select correct answer</option>
                      <option value="TRUE">TRUE</option>
                      <option value="FALSE">FALSE</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="themeupdate-input"
                      value={formData.correct_answer}
                      onChange={(e) => handleFormChange("correct_answer", e.target.value)}
                      placeholder="Enter correct answer"
                      maxLength={50}
                    />
                  )}
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Answer ID (number only)</label>
                  <input
                    type="number"
                    className="themeupdate-input"
                    value={formData.type_question === "typetext" ? "1" : formData.answer_id}
                    onChange={(e) => {
                      if (formData.type_question === "typetext") return;
                      handleFormChange("answer_id", e.target.value);
                    }}
                    placeholder="Answer identifier (number)"
                    min="1"
                    disabled={formData.type_question === "typetext"}
                    readOnly={formData.type_question === "typetext"}
                  />
                </div>

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <label className="themeupdate-label">Did You Know (Optional, max 200 chars)</label>
                  <textarea
                    className="themeupdate-input themeupdate-textarea"
                    value={formData.did_you_know}
                    onChange={(e) => handleFormChange("did_you_know", e.target.value)}
                    placeholder="Interesting fact about the answer"
                    rows={2}
                    maxLength={200}
                  />
                </div>
                  </div>

                  <div className="themeupdate-form-actions">
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveQuestion}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : editingQuestion ? "Update Question" : "Add Question"}
                    </button>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-outline"
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingQuestion(null);
                        setFormData(emptyQuestion());
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Questions Table */}
          <div className="themeupdate-table-section">
            <h3 className="themeupdate-section-title">
              Questions List ({questions.length})
            </h3>
            
            {loading ? (
              <div className="themeupdate-loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="themeupdate-empty">
                <i className="fa-solid fa-inbox"></i>
                <p>No questions added yet. Add questions using the form above or import from CSV.</p>
              </div>
            ) : (
              <div className="themeupdate-table-wrapper">
                <table className="themeupdate-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>State</th>
                      <th>Title</th>
                      <th>Question</th>
                      <th>Type</th>
                      <th>Answer</th>
                      <th>Image</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedQuestions.map((q, index) => (
                      <tr key={q.id}>
                        <td>{(safeQuestionsPage - 1) * QUESTIONS_PAGE_SIZE + index + 1}</td>
                        <td>
                          <span className="themeupdate-state-badge">{q.state}</span>
                        </td>
                        <td className="themeupdate-td-ellipsis">{q.question_title}</td>
                        <td className="themeupdate-td-ellipsis">{q.question}</td>
                        <td>
                          <span className={`themeupdate-type-badge themeupdate-type-${q.type_question}`}>
                            {q.type_question === "radioselect" ? "MCQ" : 
                             q.type_question === "truefalse" ? "T/F" : "Text"}
                          </span>
                        </td>
                        <td className="themeupdate-td-ellipsis">{q.correct_answer}</td>
                        <td>
                          {q.image ? (
                            <div className="themeupdate-table-image-preview">
                              <img 
                                src={`${process.env.REACT_APP_S3_PATH}${q.image}`} 
                                alt="Question" 
                                className="themeupdate-table-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<span class="themeupdate-has-image"><i class="fa-solid fa-image"></i></span>';
                                }}
                              />
                            </div>
                          ) : (
                            <span className="themeupdate-no-image">-</span>
                          )}
                        </td>
                        <td>
                          <div className="themeupdate-table-actions">
                            <button
                              type="button"
                              className="themeupdate-btn-icon themeupdate-btn-icon-edit"
                              onClick={() => handleEditQuestion(q)}
                              title="Edit"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className="themeupdate-btn-icon themeupdate-btn-icon-delete"
                              onClick={() => handleDeleteQuestion(q.id)}
                              title="Delete"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalQuestionPages > 1 && (
              <div className="themeupdate-pagination">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-outline"
                  onClick={() => setQuestionsPage((p) => Math.max(1, p - 1))}
                  disabled={safeQuestionsPage <= 1}
                >
                  <i className="fa-solid fa-chevron-left"></i> Prev
                </button>
                <span className="themeupdate-pagination-info">
                  Page {safeQuestionsPage} of {totalQuestionPages}
                </span>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-outline"
                  onClick={() => setQuestionsPage((p) => Math.min(totalQuestionPages, p + 1))}
                  disabled={safeQuestionsPage >= totalQuestionPages}
                >
                  Next <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            )}

          </div>
          </>}
        </div>
      </div>

      {tourRunning && TOUR_STEPS[tourStep] && (
        <div className="admin-tour-overlay">
          <div
            className={`admin-tour-highlight${tourRect ? "" : " admin-tour-highlight--center"}`}
            style={
              tourRect
                ? {
                    top: tourRect.top - 8,
                    left: tourRect.left - 8,
                    width: tourRect.width + 16,
                    height: tourRect.height + 16,
                  }
                : undefined
            }
          />
          <div
            className="admin-tour-tooltip"
            style={
              tourRect
                ? computeTourTooltipStyle(tourRect)
                : { top: "40%", left: "50%", transform: "translate(-50%, -50%)" }
            }
          >
            <div className="admin-tour-step-count">
              Step {tourStep + 1} of {TOUR_STEPS.length}
            </div>
            <h4 className="admin-tour-title">{TOUR_STEPS[tourStep].title}</h4>
            <p className="admin-tour-description">{TOUR_STEPS[tourStep].description}</p>
            <div className="admin-tour-actions">
              <button type="button" className="admin-tour-btn admin-tour-btn-skip" onClick={endTour}>
                Skip
              </button>
              <button type="button" className="admin-tour-btn admin-tour-btn-next" onClick={nextTourStep}>
                {tourStep >= TOUR_STEPS.length - 1 ? "Finish" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeUpdate;
